import { Check, Loader2, Search } from 'lucide-react';
import * as React from 'react';

import { Button } from '@/components/ui/catalyst/button';
import { useGetTasksQuery } from '@/services/api/tasksService';
import { Task } from '@/services/types/client-intake-types';

interface TaskLinkingInterfaceProps {
  matterId: string;
  linkedTaskIds: string[];
  onLink: (taskIds: string[]) => Promise<void>;
  isLinking: boolean;
}

interface GroupedTasks {
  [groupName: string]: Task[];
}

export const TaskLinkingInterface: React.FC<TaskLinkingInterfaceProps> = ({
  matterId,
  linkedTaskIds,
  onLink,
  isLinking,
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedTaskIds, setSelectedTaskIds] = React.useState<Set<string>>(
    new Set(),
  );
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(
    new Set(),
  );

  const { data: tasksData, isLoading } = useGetTasksQuery({
    matter: matterId,
    limit: 1000,
  });

  // Filter out already linked tasks and apply search
  const availableTasks = React.useMemo(() => {
    const tasks = tasksData?.results || [];
    const linkedSet = new Set(linkedTaskIds);
    return tasks.filter(
      (task) =>
        !linkedSet.has(task.id) &&
        (searchQuery === '' ||
          task.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          task.description?.toLowerCase().includes(searchQuery.toLowerCase())),
    );
  }, [tasksData?.results, linkedTaskIds, searchQuery]);

  // Group tasks by group only
  const groupedTasks: GroupedTasks = React.useMemo(() => {
    const grouped: GroupedTasks = {};

    availableTasks.forEach((task) => {
      const groupName = task.group.name;

      if (!grouped[groupName]) {
        grouped[groupName] = [];
      }

      grouped[groupName].push(task);
    });

    return grouped;
  }, [availableTasks]);

  // Expand all groups by default
  React.useEffect(() => {
    const allGroupNames = Object.keys(groupedTasks);
    setExpandedGroups(new Set(allGroupNames));
  }, [groupedTasks]);

  const handleToggleTask = (taskId: string) => {
    setSelectedTaskIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const handleExpandGroup = (groupName: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const handleToggleGroup = (groupName: string) => {
    const tasksInGroup = groupedTasks[groupName] || [];
    const allSelected = tasksInGroup.every((task) =>
      selectedTaskIds.has(task.id),
    );

    setSelectedTaskIds((prev) => {
      const newSet = new Set(prev);
      tasksInGroup.forEach((task) => {
        if (allSelected) {
          newSet.delete(task.id);
        } else {
          newSet.add(task.id);
        }
      });
      return newSet;
    });
  };

  const handleLinkSelected = async () => {
    if (selectedTaskIds.size === 0) return;
    await onLink(Array.from(selectedTaskIds));
    setSelectedTaskIds(new Set());
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const groupNames = Object.keys(groupedTasks).sort();
  const hasAvailableTasks = groupNames.length > 0;

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Search tasks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Action buttons */}
      {selectedTaskIds.size > 0 && (
        <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <span className="text-sm font-medium text-blue-900">
            {selectedTaskIds.size} task{selectedTaskIds.size !== 1 ? 's' : ''}{' '}
            selected
          </span>
          <div className="flex gap-2">
            <Button
              plain
              onClick={() => setSelectedTaskIds(new Set())}
              disabled={isLinking}
            >
              Clear
            </Button>
            <Button onClick={handleLinkSelected} disabled={isLinking}>
              {isLinking ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Linking...
                </>
              ) : (
                `Link ${selectedTaskIds.size} task${selectedTaskIds.size !== 1 ? 's' : ''}`
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Task groups */}
      <div className="border border-slate-200 rounded-lg  max-h-[80vh] ">
        {!hasAvailableTasks && (
          <div className="p-8 text-center text-sm text-slate-500">
            {searchQuery
              ? 'No tasks found matching your search'
              : 'No available tasks to link'}
          </div>
        )}

        {groupNames.map((groupName) => {
          const isGroupExpanded = expandedGroups.has(groupName);
          const tasksInGroup = groupedTasks[groupName];
          const allSelected = tasksInGroup.every((task) =>
            selectedTaskIds.has(task.id),
          );
          const someSelected = tasksInGroup.some((task) =>
            selectedTaskIds.has(task.id),
          );

          return (
            <div
              key={groupName}
              className="border-b border-slate-200 last:border-b-0"
            >
              {/* Group header with select all and expand/collapse */}
              <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors">
                <button
                  type="button"
                  onClick={() => handleToggleGroup(groupName)}
                  className={`flex-shrink-0 h-4 w-4 rounded border-2 flex items-center justify-center transition-colors ${
                    allSelected || someSelected
                      ? 'bg-blue-600 border-blue-600'
                      : 'border-slate-300 hover:border-slate-400'
                  } ${someSelected && !allSelected ? 'opacity-50' : ''}`}
                >
                  {(allSelected || someSelected) && (
                    <Check className="h-3 w-3 text-white" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => handleExpandGroup(groupName)}
                  className="flex-1 flex items-center justify-between text-left"
                >
                  <span className="font-semibold text-sm text-slate-900">
                    {groupName} ({tasksInGroup.length})
                  </span>
                  <svg
                    className={`h-4 w-4 text-slate-500 transition-transform ${
                      isGroupExpanded ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>

              {/* Individual tasks */}
              {isGroupExpanded && (
                <div className="bg-white divide-y divide-slate-100">
                  {tasksInGroup.map((task) => {
                    const isSelected = selectedTaskIds.has(task.id);

                    return (
                      <button
                        key={task.id}
                        type="button"
                        onClick={() => handleToggleTask(task.id)}
                        className="w-full px-4 py-2 pl-11 flex items-start gap-3 hover:bg-slate-50 transition-colors text-left"
                      >
                        <div
                          className={`flex-shrink-0 h-4 w-4 rounded border-2 flex items-center justify-center transition-colors mt-0.5 ${
                            isSelected
                              ? 'bg-blue-600 border-blue-600'
                              : 'border-slate-300 hover:border-slate-400'
                          }`}
                        >
                          {isSelected && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">
                            {task.name}
                          </div>
                          {task.description && (
                            <div className="text-xs text-slate-500 line-clamp-1 mt-0.5">
                              {task.description}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
